import { useTranslation, untranslated } from "@i18n";

import { forwardRef, useEffect, useRef, useState, type ComponentProps, type ComponentRef } from "react";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { mileStoneServiceUrl } from "@Features/contracts/api/contracts.api";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import DateTimePickerComponent from "@CubeComponents/Mui_Form/DateTimeComponent";
import { getContractDetails, mileStoneController } from "@Features/contracts/state/contracts.actions";
import { convertDate } from "@Services/luxonHelper";
import { BiLinkExternal } from "react-icons/bi";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import { ContractTaskGrid as ContractTasks } from "@Features/forms";
import {
  DisabledIllustration,
  getContractCRUDPermission,
  mileStonStatusTemplate,
} from "@Features/commercial-shared/contracts/contractHelpers";
import { TextField } from "@mui/material";
import { Formatters, type SlickGrid as SlickGridType } from "slickgrid-react";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import SlickGrid, { typedColumns, type SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import type { ContractDetailsState } from "@Features/contracts/state/contracts.reducer";
import type {
  GetMileStoneControllerResponse,
  MileStoneControllerDeleteRequest,
} from "@ServerTypes/contracts/contracts.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/** A single milestone row as returned by the milestone controller list response. */
type MileStoneRow = NonNullable<GetMileStoneControllerResponse["result"]>[number];

/**
 * The handle this screen exposes and reads: the grid's own public ref type, so the same object can
 * be handed to `<SlickGrid ref=...>` without an assertion. `refresh` is optional on it
 * (`SlickgridExtras`) because the grid only bolts it on once `reactGridReady` has run.
 */
type MileStoneGridHandle = SlickGrid1Ref<MileStoneRow>;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/**
 * Renders the Title column with slickgrid's own tree formatter.
 *
 * `Formatters.tree` cannot be handed to `ColumnDefinition["formatter"]` directly - that signature
 * declares `columnDef: ColumnDefinition<MileStoneRow, "title">`, and `ColumnDefinition` is not
 * assignable to slickgrid's own `Column` (its `ExcelExportColumnOptions.valueParserCallback` returns
 * `unknown` where the library's `GetDataValueCallback` returns `string | number | Date |
 * ExcelColumnMetadata`). The column the library formatter wants is therefore read back off the grid
 * at the library's own type: `getColumns()[cell]` is the very object slickgrid would have passed,
 * since `appendCellHtml` calls the formatter with `this.columns[cell]`. Same wrapper as
 * `GridColumnChooser.treeCellFormatter`.
 */
function titleTreeFormatter(
  row: number,
  cell: number,
  value: string | undefined,
  // slickgrid passes its own `Column` here; the typed one is re-read off the grid below instead.
  _columnDef: unknown,
  dataContext: MileStoneRow,
  grid?: SlickGridType,
): unknown {
  const column = grid?.getColumns()[cell];
  // `Formatters.tree` reads the grid's `treeDataOptions` and calls `grid.applyHtmlCode`, so it
  // cannot run without the grid - which slickgrid always passes when it renders a cell.
  if (!grid || !column) return "";
  return Formatters.tree(row, cell, value, column, dataContext, grid);
}

/** A row of the contract-task picker modal. */
type ContractTaskRow = {
  _id?: ObjectIdString;
  title?: string;
  originalTaskId?: ObjectIdString;
  startDate?: number;
  endDate?: number;
};

/**
 * The shapes `handleChange` is called with: the toggle's `ChangeEvent`, the date picker's
 * `DateTimeChangeArgs`, the numeric box's `{ value, previousValue }`, or the dropdown's chosen
 * option value. `value` was declared `string & number` — an uninhabited type, which made every
 * comparison against it statically dead — so each arm's real value type is named here instead.
 */
type MileStoneDateChangeArgs = {
  target?: { checked?: boolean; value?: unknown; name?: string };
  value?: string | number | Date | null;
  previousValue?: number | null;
};

/**
 * The payload this screen builds for `mileStoneController`: it starts as the insert shape and is
 * mutated in place into the update shape, which the controller's request union cannot describe.
 */
type MileStoneControllerPayload = {
  action: string;
  _id?: unknown;
  contractId?: unknown;
  data: Record<string, unknown>;
};

/** Date sub-object stored on the milestone create/edit form state. */
interface MileStoneFormDate {
  timeStamp?: number | null;
  basedOnTask?: boolean;
  taskId?: string;
  taskTitle?: string;
  offSet?: number;
  dateType?: string | null;
  selectedDate?: number | null;
}

/** Local form state for the create / edit milestone modal. */
interface MileStoneFormData {
  title?: string;
  description?: string;
  mileStoneAmount?: number | string;
  percentageOfTotal?: number | string;
  contractId?: string;
  projectId?: string;
  parentId?: string | null;
  hasChild?: boolean;
  revisedAmount?: number;
  revisedPercentageOfTotal?: number;
  dateType?: string;
  mileStoneDate?: MileStoneFormDate | null;
}

/** State backing the "select task" modal. `selectedTask` is a schedule task row. */
interface ShowTaskListState {
  status: boolean;
  selectedTask: {
    _id?: string;
    title?: string;
    startDate?: number | null;
    endDate?: number | null;
  } | null;
}

interface MileStonesProps {
  contractId: ObjectIdString;
  projectId: string;
  showOnlyGrid?: boolean;
  setBtnState?: (state: boolean) => void;
  formId?: string;
  changeOrderState?: unknown;
  contractDetails: ContractDetailsState;
}

const MileStones = forwardRef(
  (
    {
      contractId,
      projectId,
      showOnlyGrid = false,
      setBtnState,
      formId,
      changeOrderState,
      contractDetails,
    }: MileStonesProps,
    // `forwardRef` hands the second argument through untyped; the parent passes a `SlickGrid`
    // handle, which is what `treeGrid` below is read as.
    ref: React.ForwardedRef<MileStoneGridHandle>,
  ) => {
    const initialData: MileStoneFormData = {
      title: "",
      description: "",
      mileStoneAmount: "",
      contractId,
      projectId,
      mileStoneDate: {},
    };
    const [mileStoneData, setMileStoneData] = useState<MileStoneFormData>(initialData);
    const dispatch = useDispatch();
    const localRef = useRef<MileStoneGridHandle | null>(null);
    // A ForwardedRef is one of three things: an object ref, a CALLBACK ref, or null when the parent
    // passed none. Only an object ref can back the `treeGrid.current` reads below, so anything else
    // falls back to the local one — asserting the object arm swallowed the other two, and every
    // `.current` read on a callback ref silently missed.
    const treeGrid = ref !== null && typeof ref === "object" ? ref : localRef;
    const taskGridRef = useRef<ComponentRef<typeof ContractTasks>>(null);
    const [showAddMileStoneModal, setShowAddMileStoneModal] = useState({
      status: false,
      isChildContract: false,
    });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [toggleStatus, setToggleStatus] = useState(false);
    const [showTaskList, setShowTaskList] = useState<ShowTaskListState>({
      status: false,
      selectedTask: null,
    });
    const [disabledRows, setDisabledRows] = useState<ObjectIdString[]>([]);
    const [toolBarBtnState, setToolBarBtnState] = useState({
      add: true,
      addChild: false,
      edit: false,
      delete: false,
    });
    const { t } = useTranslation();

    // The `contractDetails` slice holds an empty placeholder until `getContractDetails` resolves
    // (see contracts.reducer.ts), so the server-required fields it declares non-optional really are
    // absent on the first render. Read through this view wherever a pre-load guard has to stay real.
    const contractDetailsPreLoad: Partial<ContractDetailsState> = contractDetails;
    const isModuleDisabled = !contractDetailsPreLoad.modulesEnabled?.milestones;

    const [mileStoneQuery, setMileStoneQuery] = useState({
      formId,
      contractId,
      contractVersionId: contractDetails.versionId,
      params: {
        formId,
        contractId,
        contractVersionId: contractDetails.versionId,
      },
    });

    useEffect(() => {
      setMileStoneQuery({
        formId,
        contractId,
        contractVersionId: contractDetails.versionId,
        params: {
          formId,
          contractId,
          contractVersionId: contractDetails.versionId,
        },
      });
    }, [contractDetails.versionId, contractId, formId]);

    const textFieldCommonField = {
      width: "100%",
      "& .MuiOutlinedInput-input": {
        padding: "0.5rem",
      },
      "& .MuiOutlinedInput-root": {
        "&.Mui-focused fieldset": {
          borderColor: "var(--info-light-blue)",
          borderWidth: "0.0625rem",
        },
      },
    };

    const roundOffTemplate = (value?: number) => {
      return Number.parseFloat((value ?? 0).toFixed(2));
    };

    const handleInputChange = (
      args: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      fieldType: "title" | "description" | "mileStoneAmount" | "percentageOfTotal",
    ) => {
      if (fieldType == "mileStoneAmount") {
        const value = Number(args.target.value);
        const percentageOfTotal = contractDetails.budgetAmount
          ? ((value * 100) / contractDetails.budgetAmount).toFixed(2)
          : 0;
        setMileStoneData((prev) => ({
          ...prev,
          percentageOfTotal: Number(percentageOfTotal),
          [fieldType]: value,
        }));
      } else if (fieldType == "percentageOfTotal") {
        const value = Number(args.target.value);
        if (value > 100) {
          return;
        }
        const mileStoneAmount = (contractDetails.budgetAmount! * value) / 100;
        setMileStoneData((prev) => ({
          ...prev,
          mileStoneAmount: Number(mileStoneAmount),
          [fieldType]: value,
        }));
      } else {
        setMileStoneData((prev) => ({
          ...prev,
          [fieldType]: args.target.value,
        }));
      }
    };

    const handleChange = (
      args: MileStoneDateChangeArgs,
      fieldType: "basedOnTask" | "timeStamp" | "dateType" | "offSet",
    ) => {
      if (fieldType === "basedOnTask") {
        setToggleStatus(args.target!.checked!);
        if (args.target!.checked) {
          setMileStoneData((prev) => ({
            ...prev,
            mileStoneDate: {
              ...prev.mileStoneDate,
              [fieldType]: true,
              timeStamp: null,
              dateType: "startDate",
            },
          }));
        } else {
          setMileStoneData((prev) => ({
            ...prev,
            mileStoneDate: {
              ...prev.mileStoneDate,
              timeStamp: null,
              basedOnTask: false,
              dateType: null,
            },
          }));
          setShowTaskList({ status: false, selectedTask: null });
        }
      } else if (fieldType === "timeStamp") {
        setMileStoneData((prev) => ({
          ...prev,
          mileStoneDate: {
            timeStamp: new Date(args.value!).getTime(),
            basedOnTask: false,
          },
        }));
      } else if (fieldType === "dateType") {
        // The dropdown hands over its option's `value`, the only arm that carries a string.
        const dateType = typeof args.value === "string" ? args.value : null;
        let timeStamp;
        if (dateType === "startDate") {
          timeStamp = showTaskList.selectedTask?.startDate ?? null;
        } else {
          timeStamp = showTaskList.selectedTask?.endDate ?? null;
        }
        setMileStoneData((prev) => ({
          ...prev,
          mileStoneDate: {
            ...prev.mileStoneDate,
            dateType,
            timeStamp,
            selectedDate: timeStamp,
            offSet: 0,
          },
        }));
      } else if (args.previousValue != args.value) {
        // `fieldType` can only be "offSet" here — the other three arms are handled above.
        const offSet = Number(args.value);
        setMileStoneData((prev) => ({
          ...prev,
          mileStoneDate: {
            ...prev.mileStoneDate,
            offSet,
            timeStamp: (prev.mileStoneDate?.selectedDate ?? 0) + offSet * 24 * 60 * 60 * 1000,
          },
        }));
      }
    };

    const handleOpenTaskList = () => {
      setShowTaskList((prev) => ({ ...prev, status: true }));
    };

    const mileStoneDateMapper = [
      {
        heading: t("Based on Activity"),
        isVisible: true,
        component: (
          <ToggleSwitchComponent
            checked={toggleStatus || mileStoneData.mileStoneDate?.basedOnTask}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- PROP GAP: `ToggleSwitchComponentProps` (cubeComponent/interface/syncfusion.interface.ts) declares no `name`, but the component spreads its props straight onto MUI's `Switch`, which does, so the attribute really reaches the DOM. Declaring `name` there is the fix and is outside this file.
            {...({ name: "basedOnTask" } as ComponentProps<typeof ToggleSwitchComponent>)}
            onChange={(args) => handleChange(args, "basedOnTask")}
          />
        ),
      },
      {
        heading: t("Milestone Date"),
        isVisible: !toggleStatus,
        component: (
          <DateTimePickerComponent
            onChange={(args) => handleChange(args, "timeStamp")}
            value={mileStoneData.mileStoneDate?.timeStamp ? new Date(mileStoneData.mileStoneDate.timeStamp) : undefined}
            name="milestoneDate"
            width="80%"
            id="milestoneDate"
            placeholder={t("Select Date")}
            format="dd/MM/yyyy hh:mm"
          />
        ),
      },
      {
        heading: t("Select Activity"),
        isVisible: toggleStatus,
        component: (
          <div className="flex gap-3 items-center">
            <BiLinkExternal onClick={handleOpenTaskList} className="cursor-pointer" />
            <span>{showTaskList.selectedTask?.title || mileStoneData.mileStoneDate?.taskTitle}</span>
          </div>
        ),
      },
      {
        heading: t("Based on Activity's Date"),
        isVisible: toggleStatus,
        component: (
          <SelectDropDown
            onChange={(option) =>
              handleChange({ value: option && !Array.isArray(option) ? option.value : undefined }, "dateType")
            }
            value={mileStoneData.mileStoneDate?.dateType || "startDate"}
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            fields={{
              text: "name",
              value: "value",
            }}
            options={[
              { name: t("Start Date"), value: "startDate" },
              { name: t("End Date"), value: "endDate" },
            ]}
            buttonWidth="400"
            popperWidth={300}
          />
        ),
      },
      {
        heading: t("Offset (Days)"),
        isVisible: toggleStatus,
        component: (
          <NumericTextBoxComponent
            width="80%"
            value={mileStoneData.mileStoneDate?.offSet}
            name="offSet"
            placeholder={t("Select Offset")}
            change={(args) => handleChange(args, "offSet")}
          />
        ),
      },
      {
        heading: t("Date"),
        isVisible: toggleStatus,
        component: mileStoneData.mileStoneDate?.timeStamp
          ? convertDate(Number(mileStoneData.mileStoneDate.timeStamp), "dateForTooltip")
          : "",
      },
    ];

    const mileStoneParametersMapper = [
      {
        id: "title",
        heading: `${t("Title")}*`,
        component: (
          <TextFieldComponent
            showClearButton
            value={mileStoneData.title}
            placeholder={t("Enter title")}
            name="title"
            onChange={(args: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleInputChange(args, "title")
            }
          />
        ),
      },
      {
        id: "description",
        heading: `${t("Description")}*`,
        component: (
          <TextFieldComponent
            value={mileStoneData.description}
            showClearButton
            multiline
            placeholder={t("Enter description")}
            name="description"
            onChange={(args: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              handleInputChange(args, "description")
            }
          />
        ),
      },
      {
        id: "amount",
        heading: t("Amount"),
        component: (
          <TextField
            onChange={(args) => handleInputChange(args, "mileStoneAmount")}
            disabled={mileStoneData.hasChild && editMode}
            value={mileStoneData.mileStoneAmount}
            placeholder={t("Enter amount")}
            name="mileStoneAmount"
            inputProps={{
              min: 0,
              max: contractDetails.remainingBudgetAmountForMileStones! + (editMode ? mileStoneData.revisedAmount! : 0),
            }}
            sx={textFieldCommonField}
            type="number"
            focused
          />
        ),
      },
      {
        id: "%OfTotal",
        heading: t("% of Total"),
        component: (
          <TextField
            onChange={(args) => handleInputChange(args, "percentageOfTotal")}
            value={mileStoneData.percentageOfTotal}
            name="amount"
            inputProps={{
              min: 0,
              max:
                (contractDetails.remainingBudgetAmountForMileStones! * 100) / contractDetails.budgetAmount! +
                (editMode ? mileStoneData.revisedPercentageOfTotal! : 0),
            }}
            placeholder={t("Enter %")}
            disabled={mileStoneData.hasChild && editMode}
            sx={textFieldCommonField}
            type="number"
            focused
          />
        ),
      },
      {
        id: "MileStoneDate",
        heading: t("Mile Stone Date"),
        component: (
          <div className="flex w-full flex-col gap-5 p-2 items-center">
            {mileStoneDateMapper.map(
              (field, index) =>
                field.isVisible && (
                  <span key={index} className="w-full flex">
                    <span className="flex flex-1">{field.heading}</span>
                    <span className="flex flex-1">{field.component}</span>
                  </span>
                ),
            )}
          </div>
        ),
      },
    ];

    const handleAddMileStoneModalShow = () => {
      setShowAddMileStoneModal((prev) => {
        if (prev.status) {
          setMileStoneData(initialData);
          setEditMode(false);
          setToggleStatus(false);
          closeTaskListModal();
        }
        return {
          status: !prev.status,
          isChildContract: false,
        };
      });
    };

    const showHideDeleteModal = () => {
      setShowDeleteModal((prev) => !prev);
    };

    const expandBtns = [
      {
        name: t("ExpandAll"),
        prefixIcon: "Expand",
        id: "expandAll",
      },
      {
        name: t("CollapseAll"),
        prefixIcon: "Collapse",
        id: "collapseAll",
      },
    ];
    const toolBarItems = [
      {
        name: t("Create"),
        prefixIcon: "add",
        id: "create",
        disabled: !getContractCRUDPermission(contractDetails, changeOrderState),
      },
      {
        name: t("Create Child"),
        prefixIcon: "add",
        id: "create_child",
        disabled: !toolBarBtnState.addChild,
      },
      {
        name: t("Edit"),
        prefixIcon: "editProfile",
        id: "edit",
        disabled: !toolBarBtnState.edit,
      },
      {
        name: t("Delete"),
        prefixIcon: "delete",
        id: "delete",
        disabled: !toolBarBtnState.delete,
      },
      ...expandBtns,
    ];

    const createMileStoneClick = async () => {
      // The controller takes a discriminated insert/update payload; this builds the insert shape
      // and mutates it into the update shape below, so the union is named once here.
      const data: MileStoneControllerPayload = {
        action: "insert",
        data: {
          ...mileStoneData,
        },
      };
      if (editMode) {
        data.action = "update";
        delete data.data.contractId;
        delete data.data.percentageOfTotal;
        delete data.data.projectId;
        // `getAllSelectedItems()` is declared dense, but SlickDataView pushes `getItemById(id)` for
        // every id still in `selectedRowIds` — `undefined` across a refresh/filter.
        const selectedItems: Array<MileStoneRow | undefined> = treeGrid.current!.dataView.getAllSelectedItems();
        data._id = selectedItems[0]?._id;
        data.contractId = contractId;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `data.data` is the whole local form state spread verbatim: `percentageOfTotal`, `projectId`, `hasChild`, `revisedAmount`, `revisedPercentageOfTotal` and `dateType` are not declared by `getMileStoneInsertSchema.data`, and the form spells the task id `taskId` where the schema spells it `taksId`. Enumerating an exact payload here would change what goes on the wire; the fix is to reconcile the form state with the schema.
      const response = await mileStoneController(data as Parameters<typeof mileStoneController>[0]);
      if (response.data.status) {
        const content = editMode
          ? t("Milestone {{title}} updated successfully", { title: mileStoneData.title })
          : t("Milestone {{title}} created successfully", { title: mileStoneData.title });
        dispatch(
          showToastNotification({
            content,
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        treeGrid.current?.refresh?.();
        setMileStoneData(initialData);
        setEditMode(false);
        setToggleStatus(false);
        handleAddMileStoneModalShow();
        dispatch(getContractDetails({ contractId }));
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(response.data.errorMessage),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    };

    const toolbarClickHandler = async (args: string) => {
      const selectedItems: Array<MileStoneRow | undefined> = treeGrid.current!.dataView.getAllSelectedItems();
      const selectedData = selectedItems[0];

      if (args === "create") {
        handleAddMileStoneModalShow();
      }

      if (args === "create_child") {
        setMileStoneData((prev) => ({
          ...prev,
          parentId: selectedData?.mileStoneId,
        }));
        setShowAddMileStoneModal(() => ({
          status: true,
          isChildContract: true,
        }));
      }
      if (args === "edit") {
        setShowAddMileStoneModal((prev) => ({
          status: !prev.status,
          isChildContract: false,
        }));
        setEditMode(true);
        setMileStoneData({
          title: selectedData?.title,
          hasChild: selectedData?.hasChild,
          description: selectedData?.description,
          mileStoneAmount: selectedData?.revisedAmount,
          percentageOfTotal: selectedData?.revisedPercentageOfTotal,
          mileStoneDate: selectedData?.mileStoneDate,
          parentId: selectedData?.parentId,
        });
        if (selectedData?.mileStoneDate?.basedOnTask) {
          setToggleStatus(true);
        }
      }
      if (args === "delete") {
        showHideDeleteModal();
      } else if (args === "collapseAll") {
        treeGrid.current?.treeDataService.toggleTreeDataCollapse(true);
      } else if (args === "expandAll") {
        treeGrid.current?.treeDataService.toggleTreeDataCollapse(false);
      }
    };

    const selectionHandler = () => {
      const selectedData: Array<MileStoneRow | undefined> = treeGrid.current!.dataView.getAllSelectedItems();

      if (setBtnState) {
        if (selectedData.length > 0) {
          setBtnState(selectedData.every((item) => !item?.isAlreadyAddedInThisForm));
        } else {
          setBtnState(false);
        }
      }
      if (showOnlyGrid || !getContractCRUDPermission(contractDetails, changeOrderState)) {
        return;
      }
      const totalRowsSelected = selectedData.length;
      setToolBarBtnState((prev) => ({
        ...prev,
        addChild: totalRowsSelected == 1,
        edit: totalRowsSelected == 1,
        delete: totalRowsSelected >= 1,
      }));
    };

    const deleteMileStone = async () => {
      const selectedMileStones: Array<MileStoneRow | undefined> = treeGrid.current!.dataView.getAllSelectedItems();
      // A hole in the selection carries no id to delete, so it is dropped rather than sent as a null
      // the server would refuse the whole batch over.
      const mileStoneIds = selectedMileStones.flatMap((mileStone) => mileStone?.mileStoneId ?? []);
      const data: MileStoneControllerDeleteRequest = {
        action: "delete",
        mileStoneIds,
        contractId,
      };
      const isAnyMileStoneAddedInForm = selectedMileStones.some((milestone) => milestone?.isAddedInForms);
      if (isAnyMileStoneAddedInForm) {
        dispatch(
          showToastNotification({
            content: t(
              "One or more of the selected milestone(s) is already added in forms and hence cannot be removed",
            ),
            type: TOAST_TYPE_ERROR,
          }),
        );
      } else {
        const res = await mileStoneController(data);
        if (res.data.status) {
          dispatch(
            showToastNotification({
              content: t("Selected milestone(s) deleted successfully"),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
          showHideDeleteModal();
          treeGrid.current?.refresh?.();
          dispatch(getContractDetails({ contractId }));
        } else {
          dispatch(
            showToastNotification({
              content: untranslated(res.data.message ?? ""),
              type: TOAST_TYPE_ERROR,
            }),
          );
        }
      }
    };

    const dateTemplate = (args: MileStoneRow) => {
      if (args.mileStoneDate?.timeStamp == null) {
        return "-";
      }
      return convertDate(Number(args.mileStoneDate.timeStamp), "dateForTooltip");
    };
    const completionDateTemplate = (args: MileStoneRow) => {
      if (args.completionDate == null) {
        return "-";
      }
      return convertDate(Number(args.completionDate), "dateForTooltip");
    };

    const columnDefinitions = useRef(
      typedColumns<MileStoneRow>([
        {
          id: "title",
          name: t("Title"),
          field: "title",
          minWidth: 150,
          filterType: "Text",
          formatter: titleTreeFormatter,
        },
        {
          id: "description",
          name: t("Description"),
          field: "description",
          minWidth: 120,
          filterType: "Text",
        },
        {
          id: "mileStoneDate",
          name: t("Milestone Date"),
          field: "mileStoneDate",
          minWidth: 180,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) => dateTemplate(args),
        },
        {
          id: "completionDate",
          name: t("Completion Date"),
          field: "completionDate",
          minWidth: 180,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            completionDateTemplate(args),
        },
        {
          id: "status",
          name: t("Status"),
          field: "status",
          minWidth: 180,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            mileStonStatusTemplate(args.status, true),
        },
        {
          id: "mileStoneAmount",
          name: `${t("Amount")}`,
          columnGroup: "Milestone",
          field: "mileStoneAmount",
          minWidth: 100,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.mileStoneAmount),
        },
        {
          id: "mileStonePercentageOfTotal",
          name: `${t("% Of Total")}`,
          field: "mileStonePercentageOfTotal",
          columnGroup: "Milestone",
          minWidth: 100,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.mileStonePercentageOfTotal),
        },
        {
          id: "changeOrderAmount",
          name: `${t("Amount")}`,
          field: "changeOrderAmount",
          columnGroup: "Change Order",
          minWidth: 100,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.changeOrderAmount),
        },
        {
          id: "changeOrderPercentageOfTotal",
          name: `${t("% Of Total")}`,
          field: "changeOrderPercentageOfTotal",
          columnGroup: "Change Order",
          minWidth: 100,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.changeOrderPercentageOfTotal),
        },
        {
          id: "revisedAmount",
          name: `${t("Amount")}`,
          field: "revisedAmount",
          minWidth: 100,
          columnGroup: "Revised",
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.revisedAmount),
        },
        {
          id: "revisedPercentageOfTotal",
          name: `${t("% Of Total")}`,
          field: "revisedPercentageOfTotal",
          minWidth: 100,
          columnGroup: "Revised",
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.revisedPercentageOfTotal),
        },
        {
          id: "usedAmount",
          name: `${t("Amount")}`,
          field: "usedAmount",
          minWidth: 100,
          columnGroup: "Used",
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.usedAmount),
        },
        {
          id: "usedPercentageOfTotal",
          name: `${t("% Of Total")}`,
          field: "usedPercentageOfTotal",
          minWidth: 100,
          columnGroup: "Used",
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.usedPercentageOfTotal),
        },
        {
          id: "remainingAmount",
          name: `${t("Amount")}`,
          field: "remainingAmount",
          columnGroup: "Remaining Amount",
          minWidth: 100,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.remainingAmount),
        },
        {
          id: "remainingPercentageOfTotal",
          name: `${t("% Of Total")}`,
          field: "remainingPercentageOfTotal",
          columnGroup: "Remaining Amount",
          minWidth: 100,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.remainingPercentageOfTotal),
        },
        {
          id: "inspectedAmount",
          name: `${t("Amount")}`,
          field: "inspectedAmount",
          columnGroup: "Inspected",
          minWidth: 100,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.inspectedAmount),
        },
        {
          id: "inspectedPercentageOfTotal",
          name: `${t("% Of Total")}`,
          field: "inspectedPercentageOfTotal",
          columnGroup: "Inspected",
          minWidth: 100,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.inspectedPercentageOfTotal),
        },
        {
          id: "billedAmount",
          name: `${t("Amount")}`,
          field: "billedAmount",
          columnGroup: "Billed",
          minWidth: 100,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.billedAmount),
        },
        {
          id: "billedPercentageOfTotal",
          name: `${t("% Of Total")}`,
          field: "billedPercentageOfTotal",
          columnGroup: "Billed",
          minWidth: 100,
          formatter: (data: number, value: number, a: unknown, b: unknown, args: MileStoneRow) =>
            roundOffTemplate(args.billedPercentageOfTotal),
        },
      ]),
    );

    /**
     * The grid hands this hook one of three shapes (see the `beforeDataBound` doc on
     * `SlickGridProps`): the whole remote response, an already-unwrapped `{ result }`, or the rows
     * themselves. Read at `unknown` — which that prop explicitly still accepts — and shape-checked
     * here, so none of the three is assumed. It used to read `data.data!.result` and hand the
     * `undefined` that produced straight to `setDisabledRows`.
     */
    const beforeDataBound = (resp: unknown): MileStoneRow[] => {
      let rows: MileStoneRow[] = [];
      if (Array.isArray(resp)) {
        rows = resp;
      } else if (isRecord(resp)) {
        const wrapped = isRecord(resp.data) ? resp.data.result : resp.result;
        if (Array.isArray(wrapped)) {
          rows = wrapped;
        }
      }
      setDisabledRows(
        rows.flatMap((item) => (item.isAlreadyAddedInThisForm && !item.hasChild ? (item.mileStoneId ?? []) : [])),
      );
      return rows;
    };

    const getGridComponent = () => {
      return (
        <SlickGrid
          ref={treeGrid}
          columnDefinitions={columnDefinitions.current}
          treeGrid
          enableWrapText
          disabledIds={disabledRows}
          disableCustomSorting
          treeColIndex={1}
          gridOptions={{
            showPreHeaderPanel: true,
            preHeaderPanelHeight: 30,
            topHeaderPanelHeight: 30,
            frozenColumn: 5,
            enableAutoResize: true,
            showHeaderRow: false,
            enableSorting: true,
          }}
          allowPagination={false}
          idMapping="mileStoneId"
          hasChildMapping="hasChild"
          parentIdMapping="parentId"
          initialSortColumnId="createdDate"
          onSelectedRowIdsChanged={selectionHandler}
          toolbar={showOnlyGrid ? expandBtns : toolBarItems}
          toolbarClickHandler={toolbarClickHandler}
          remoteDataSource={mileStoneServiceUrl}
          query={mileStoneQuery}
          beforeDataBound={beforeDataBound}
          enableFiltering={true}
          enableFilteringOption
          enableHeaderGrouping={true}
        />
      );
    };
    const deleteModalFooter = () => (
      <div className="flex gap-4">
        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={deleteMileStone}>
          {t("Delete")}
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={showHideDeleteModal}>
          {t("Cancel")}
        </ButtonComponent>
      </div>
    );
    const deleteModalHeader = () => (
      <div className="w-full flex justify-between rounded-md">
        <div className="flex  flex-col gap-1">{t("Delete Milestones(s)")}</div>
      </div>
    );
    const deleteModalContent = () => (
      <div className="flex  flex-col gap-10">
        <span>{t("All Child Milestones will also be deleted. Are you sure you want to continue")}?</span>
      </div>
    );

    const addModalHeader = () => (
      <div className="w-full flex items-center justify-between">
        <span className="flex flex-col ">
          <span>{editMode ? t("Edit Milestone") : t("Create Milestone")}</span>
          {showAddMileStoneModal.isChildContract && (
            <span className="text-12">
              {t(
                "By adding a nested milestone, the Amount & % fields of the parent milestone will get overridden based on the sum of child values.",
              )}
            </span>
          )}
        </span>
      </div>
    );
    const addModalContent = () => {
      let amountContent = t("Available amount : {{amount}}", {
        amount: contractDetails.remainingBudgetAmountForMileStones,
      });
      let percentContent = t("Available % of total : {{percent}}", {
        percent: contractDetails.budgetAmount
          ? ((contractDetails.remainingBudgetAmountForMileStones! * 100) / contractDetails.budgetAmount).toFixed(2)
          : 0,
      });
      if (editMode && treeGrid.current!.dataView.getAllSelectedItems()[0]?.hasChild) {
        amountContent = t("Parent milestone's amount cannot be directly changed");
        percentContent = t("Parent milestone's % of total cannot be directly changed");
      }
      return (
        <div className=" flex flex-col gap-5">
          {mileStoneParametersMapper.map((data, index) => (
            <div key={index} className="flex w-full">
              <span className="flex-1 font-semibold">
                <span className="flex gap-5">
                  {data.heading}
                  {(data.id == "amount" || data.id == "%OfTotal") && (
                    <span style={{ width: "fit-content" }}>
                      <IconComponent
                        name="details"
                        width="1.5rem"
                        height="1.5rem"
                        position="bottom"
                        toolTipContent={data.id == "amount" ? amountContent : percentContent}
                      />
                    </span>
                  )}
                </span>
              </span>
              <span className="flex-2">{data.component}</span>
            </div>
          ))}
        </div>
      );
    };
    const addModalFooter = () => {
      let isDisabled = false;
      const { title, description, mileStoneAmount, mileStoneDate } = mileStoneData;
      if (
        title == "" ||
        description == "" ||
        mileStoneAmount == "" ||
        !getContractCRUDPermission(contractDetails, changeOrderState) ||
        // `== null` already covers undefined.
        mileStoneDate!.timeStamp == null
      ) {
        isDisabled = true;
      }
      return (
        <div className="">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            disabled={isDisabled}
            onClick={createMileStoneClick}
          >
            {editMode ? t("Save") : t("Create")}
          </ButtonComponent>
        </div>
      );
    };

    const closeTaskListModal = () => {
      setShowTaskList((prev) => ({
        ...prev,
        status: false,
        selectedTask: null,
      }));
    };

    const handleTaskSelect = () => {
      // `SlickgridReactInstance.dataView` is the vendor `SlickDataView<any>`, and its
      // `getAllSelectedItems()` holds a hole for every id still in `selectedRowIds` that no longer
      // resolves; the grid's row type and those holes are both stated here, at the single read.
      const selectedTasks: Array<ContractTaskRow | undefined> =
        taskGridRef.current?.dataView.getAllSelectedItems() ?? [];
      const selectedTask = selectedTasks[0];
      if (selectedTask) {
        const date = mileStoneData.dateType === "endDate" ? selectedTask.endDate : selectedTask.startDate;
        setMileStoneData((prev) => ({
          ...prev,
          mileStoneDate: {
            ...prev.mileStoneDate,
            timeStamp: date,
            selectedDate: date,
            taskId: selectedTask._id,
            taskTitle: selectedTask.title,
            offSet: 0,
          },
        }));

        setShowTaskList({ status: false, selectedTask });
      } else {
        dispatch(
          showToastNotification({
            content: t("Select task first"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    };

    const getTaskList = () => (
      <ModalStateDialogComponent
        allowModalPadding={false}
        footerContent={
          contractDetails.linkedSchedule?._id ? (
            <div className="">
              <ButtonComponent onClick={handleTaskSelect} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
                {t("Select")}
              </ButtonComponent>
            </div>
          ) : undefined
        }
        width="70%"
        height={"80%"}
        headerTitle={<div className="flex w-full justify-between items-center">{t("Select Activity")}</div>}
        onCancel={closeTaskListModal}
        isVisible={showTaskList.status}
        modelContent={<ContractTasks allowCheckBoxSelection={false} ref={taskGridRef} contractId={contractId} />}
      />
    );

    return (
      <>
        {showAddMileStoneModal.status && (
          <ModalStateDialogComponent
            footerContent={addModalFooter()}
            width="50%"
            allowModalPadding={false}
            headerTitle={addModalHeader()}
            onCancel={handleAddMileStoneModalShow}
            isVisible
            modelContent={addModalContent()}
          />
        )}
        {showDeleteModal && (
          <ModalStateDialogComponent
            footerContent={deleteModalFooter()}
            width="30%"
            headerTitle={deleteModalHeader()}
            onCancel={showHideDeleteModal}
            isVisible
            modelContent={deleteModalContent()}
          />
        )}
        <div className="flex overflow-auto w-full h-full flex-col">
          {isModuleDisabled ? <DisabledIllustration /> : getGridComponent()}
        </div>
        {showTaskList.status && getTaskList()}
      </>
    );
  },
);
MileStones.displayName = "MileStones";
export default MileStones;
